import { useEffect } from "react";
import { useForm, FieldValues, Path } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";
import { RootState } from "@/redux/store";
import { capitalize, getBaseRoute, getRequiredFields } from "@/lib/utils";
import { MasterAddEditConfig } from "./types";

const useAddEditMasterModule = <TFormValues extends FieldValues>({
  moduleName,
  moduleRoute,
  schema,
  defaultValues,
  nameFieldKey,
  descFieldKey = "description" as Path<TFormValues>,
  queryKeyToInvalidate,
  useGetByIdQuery,
  useAddMutation,
  useUpdateMutation,
  module,
  arrayCustomFieldKeys = []
}: MasterAddEditConfig<TFormValues>) => {
  const { id, action } = useParams();
  const isView = action === "view";
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const basePath = getBaseRoute(useLocation().pathname);
  const communityData = useSelector((state: RootState) => state.currentData.communityData);

  const form = useForm<TFormValues>({
    resolver: zodResolver<any>(schema),
    defaultValues,
  });

  const requiredFields = getRequiredFields(schema);

  const breadcrumbData = [
    {
      link: `/${basePath}/${moduleRoute}`,
      name: moduleName,
    },
    {
      link: "",
      name: id ? `${capitalize(action as string)} ${moduleName}` : `Add ${moduleName}`,
    },
  ];

  const onBackClick = () => {
    navigate(`/${basePath}/${moduleRoute}`);
  };

  // Fetch record details if editing
  const { data: recordData, isLoading: isRecordLoading } = useGetByIdQuery({
    communityId: communityData?.communityId,
    id: id as string,
    module: module as string,
    queryParams: { enabled: !!id },
  });

  // Populate form values when data arrives
  useEffect(() => {
    if (recordData?.data) {
      const data = recordData.data;

      // 1. Convert primitives to strings, while safely handling arrays and objects
      const formattedData = Object.entries(data).reduce((acc, [key, value]) => {
        if (value === null || value === undefined) {
          acc[key] = "";
        } else if (Array.isArray(value)) {
          acc[key] = value; // Keep arrays as arrays
        } else if (typeof value === "object") {
          acc[key] = value;
        } else if (key == "requirementType") {
          acc[key] = value == "REQUIRED";
        } else if (key == "active") {
          acc[key] = value;
        } else {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, any>);

      // 2. Extract array of string IDs for multi-select custom fields
      arrayCustomFieldKeys.forEach(({ apiKey, formKey, idKey }) => {
        if (Array.isArray(data[apiKey])) {
          formattedData[formKey] = data[apiKey].map((item: any) =>
            typeof item === "object" && item !== null
              ? String(item[idKey])
              : String(item)
          );
        }
      });

      form.reset({
        ...formattedData,
        [nameFieldKey as string]: formattedData[nameFieldKey as string] || "",
        [descFieldKey as string]: formattedData[descFieldKey as string] || "",
      } as unknown as TFormValues);
    }
  }, [recordData, nameFieldKey, descFieldKey, JSON.stringify(arrayCustomFieldKeys)])

  // Add Mutation
  const { mutate: addInfo, isPending: isAddPending } = useAddMutation({
    onError: (error?: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
    onSuccess: () => {
      toast.success(`${moduleName} Added Successfully`);
      queryClient.invalidateQueries({ queryKey: [queryKeyToInvalidate] });
      onBackClick();
    },
  });

  // Update Mutation
  const { mutate: updateInfo, isPending: isUpdatePending } = useUpdateMutation({
    onError: (error?: any) => {
      toast.error(error?.response?.data?.message || "Something went wrong");
    },
    onSuccess: () => {
      toast.success(`${moduleName} Updated Successfully`);
      queryClient.invalidateQueries({ queryKey: [queryKeyToInvalidate] });
      onBackClick();
    },
  });

  const isPropertyZone = queryKeyToInvalidate == "property-zones";
  const isOcpDocTypes = queryKeyToInvalidate == "ocp-document-types";

  const onSubmit = (data: TFormValues) => {
    const dataObj = {
      communityId: communityData?.communityId,
      module,
      data: {
        ...(isPropertyZone ? {zones: [{...data, zoneId: id}]} : data),
        ...(isOcpDocTypes ? {requirementType: data?.requirementType ? "REQUIRED": "OPTIONAL"} : {}),
        communityId: communityData?.communityId
      },
    };

    if (id) {
      updateInfo({
        id,
        ...dataObj
      });
    } else {
      addInfo({
        ...dataObj
      });
    }
  };

  return {
    breadcrumbData,
    onBackClick,
    form,
    onSubmit,
    isAddPending,
    isUpdatePending,
    requiredFields,
    onCancel: onBackClick,
    isRecordLoading,
    nameFieldKey,
    descFieldKey,
    isView,
  };
};

export default useAddEditMasterModule;